// Раскадровка ролика для таймлайна шота и ленты монтажа: кадры рисует сам браузер (скрытый <video> + перемотка),
// без сервера; готовые раскадровки кэшируются по ролику и числу кадров (несколько последних), чтобы возврат к шоту
// или на монтаж не качал ролик заново.
import { useEffect, useMemo, useState } from 'react'

export const FRAME_H = 60
export const MAX_FRAMES = 24
// Готовые раскадровки — по ролику и числу кадров (несколько последних), чтобы возврат к шоту не качал ролик заново.
// Кэш общий для таймлайна шота и ленты монтажа; при 10+ клипах старые записи вытесняются, поэтому показ не может
// опираться на сам кэш (см. useFilmstrip: снимок на момент ключа).
const CACHE_MAX = 16
const filmstripCache = new Map<string, HTMLCanvasElement[]>()
function rememberFilmstrip(key: string, frames: HTMLCanvasElement[]) {
	filmstripCache.delete(key)
	filmstripCache.set(key, frames)
	while (filmstripCache.size > CACHE_MAX) filmstripCache.delete(filmstripCache.keys().next().value as string)
}
/** Только для тестов: кэш готовых раскадровок. */
export const __filmstripCache = filmstripCache

function waitFor(el: HTMLVideoElement, event: string, ms = 10000): Promise<void> {
	return new Promise((resolve, reject) => {
		const t = window.setTimeout(() => { off(); reject(new Error('timeout')) }, ms)
		const ok = () => { off(); resolve() }
		const fail = () => { off(); reject(new Error('video error')) }
		const off = () => { window.clearTimeout(t); el.removeEventListener(event, ok); el.removeEventListener('error', fail) }
		el.addEventListener(event, ok, { once: true })
		el.addEventListener('error', fail, { once: true })
	})
}

/** Кадры ролика для раскадровки: count штук через равные промежутки, по мере готовности (null — ещё рисуется). */
export function useFilmstrip(cacheKey: string | null, url: string | null, count: number, aspect: number): (HTMLCanvasElement | null)[] {
	const key = cacheKey && url && count > 0 ? `${cacheKey}:${count}` : null
	const [state, setState] = useState<{ key: string; frames: (HTMLCanvasElement | null)[] } | null>(null)
	// Готовая раскадровка из кэша — снимком на момент ключа: кэш общий и вытесняет старые записи (владелец 07.10:
	// «пропадают кадры» — у клипов, взятых из кэша, кадры исчезали, когда их запись вытесняли клипы-соседи).
	const cached = useMemo(() => (key ? filmstripCache.get(key) ?? null : null), [key])
	useEffect(() => {
		if (!key || !url || cached || filmstripCache.has(key)) return
		let alive = true
		const v = document.createElement('video')
		v.muted = true
		v.playsInline = true
		v.preload = 'auto'
		v.src = url
		const frames: (HTMLCanvasElement | null)[] = Array.from({ length: count }, () => null)
		const dpr = Math.min(2, window.devicePixelRatio || 1)
		const h = Math.round(FRAME_H * dpr), w = Math.max(1, Math.round(FRAME_H * aspect * dpr))
		void (async () => {
			try {
				if (v.readyState < 1) await waitFor(v, 'loadedmetadata')
				const d = v.duration
				if (!Number.isFinite(d) || d <= 0) return
				for (let i = 0; i < count && alive; i++) {
					v.currentTime = Math.min(Math.max(0, d - 0.05), ((i + 0.5) * d) / count)
					await waitFor(v, 'seeked')
					if (!alive) return
					const c = document.createElement('canvas')
					c.width = w
					c.height = h
					c.getContext('2d')?.drawImage(v, 0, 0, w, h)
					frames[i] = c
					setState({ key, frames: [...frames] })
				}
				if (alive && frames.every(Boolean)) rememberFilmstrip(key, frames as HTMLCanvasElement[])
			} catch {
				// Ролик не открылся или долго грузится — раскадровки не будет, полоса остаётся линией.
			}
		})()
		return () => {
			alive = false
			v.removeAttribute('src')
			v.load()
		}
	}, [key, url, count, aspect, cached])
	if (!key) return []
	return state?.key === key ? state.frames : cached ?? filmstripCache.get(key) ?? Array.from({ length: count }, () => null)
}
